/**
 * RTK Query endpoints for storage markdown + partial content edits (epic-6 §4,
 * ST-3/ST-4). Base path `/storage/*` (dev proxy → storage-service :4100).
 */
import { apiSlice } from './apiSlice';
import type {
  CreateMarkdownDto,
  SaveMarkdownDto,
  PartialFileContentOp,
  FileEditJobResult,
  StorageFile,
  StorageFilePresignResult,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

export const storageContentApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** POST /storage/collections/:id/markdown — create a .md file from the editor. */
    createCollectionMarkdown: builder.mutation<
      StorageFile,
      { collectionId: string; body: CreateMarkdownDto }
    >({
      query: ({ collectionId, body }) => ({
        url: `/storage/collections/${collectionId}/markdown`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: StorageFile | { data: StorageFile }) =>
        (response as { data?: StorageFile }).data ?? (response as StorageFile),
      invalidatesTags: [
        { type: 'StorageFile', id: 'LIST' },
        { type: 'StorageCollection', id: 'LIST' },
      ],
    }),

    /** PUT /storage/files/:id/markdown — full replace via the reupload path. */
    saveFileMarkdown: builder.mutation<
      StorageFile,
      { fileId: string; body: SaveMarkdownDto }
    >({
      query: ({ fileId, body }) => ({
        url: `/storage/files/${fileId}/markdown`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: StorageFile | { data: StorageFile }) =>
        (response as { data?: StorageFile }).data ?? (response as StorageFile),
      invalidatesTags: (_result, _error, { fileId }) => [
        { type: 'StorageFile', id: fileId },
        { type: 'StorageFile', id: 'LIST' },
      ],
    }),

    /** PATCH /storage/files/:id/content — append / overwrite / remove range. */
    editFileContent: builder.mutation<
      FileEditJobResult,
      { fileId: string; body: PartialFileContentOp }
    >({
      query: ({ fileId, body }) => ({
        url: `/storage/files/${fileId}/content`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { fileId }) => [
        { type: 'StorageFile', id: fileId },
        { type: 'StorageFile', id: 'LIST' },
      ],
    }),

    /**
     * POST /storage/files/:id/presign — short-lived share link (AC-12).
     * s3 replicas return a native presigned URL (`oneUse: false`); other
     * providers fall back to a one-time `otk` URL (`oneUse: true`). Read-only
     * action: no cache to invalidate.
     */
    presignFile: builder.mutation<
      StorageFilePresignResult,
      { fileId: string; ttlSeconds?: number }
    >({
      query: ({ fileId, ttlSeconds = 900 }) => ({
        url: `/storage/files/${fileId}/presign`,
        method: 'POST',
        body: { ttlSeconds },
      }),
      invalidatesTags: [],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useCreateCollectionMarkdownMutation,
  useSaveFileMarkdownMutation,
  useEditFileContentMutation,
  usePresignFileMutation,
} = storageContentApi;